/**
 * poolMatch.js - resolve a roughly-read player name to a pool entry.
 *
 * 🔴 WHY THIS EXISTS, AND WHY IT IS NOT AN OCR PROBLEM. A screenshot reader
 * produces mangled names: dropped apostrophes, l/I/1 and O/0 swaps, joined
 * tokens, abbreviated first names. A general OCR pipeline answers that with a
 * better engine, because its output space is unconstrained prose. Ours is not.
 * ENTRY_POOL is a CLOSED vocabulary of roughly three hundred entries, so every
 * name the reader emits either IS one of them or is not a name at all. That
 * flips the whole problem: the engine only has to be roughly right, and this
 * file carries the accuracy. It is why a free in-browser engine is enough and
 * a paid cloud one would buy nothing.
 *
 * 🔴 ENTRY_POOL IS READ AT CALL TIME, NEVER SNAPSHOTTED. The pool is derived at
 * module load from the projection set (see rateEntryPool.js), so the next
 * `npm run gen-projections` changes it. A cached candidate list here would be
 * right today and silently wrong after the next regeneration, which is exactly
 * the failure that hid twenty-five unenterable players until the b6d60a3 audit.
 *
 * 🔴 POSITION IS A FILTER, NOT A TIEBREAKER. A row the screenshot says is an RB
 * never matches a QB, however good the string looks. When the screenshot gives
 * no position, there is no position; this file never guesses one from a name.
 *
 * 🔴 NORMALIZATION IS normName's JOB AND ONLY normName's JOB. playerNameNorm.js
 * already collapses accents, apostrophes, punctuation and generational suffixes,
 * and its header explains why a second copy diverges silently. Everything below
 * compares strings that have been through it.
 */

import { normName } from "./playerNameNorm.js";
import { ENTRY_POOL } from "./rateEntryPool.js";
import { projGroup } from "./rateProjection.js";

/**
 * At or above this, the UI fills the row and says nothing. Below it but at or
 * above UNSURE, the UI fills the row AND flags it. Below UNSURE, nothing is
 * filled and the raw text is handed back to the user to type.
 *
 * 🔴 THE THRESHOLDS LIVE HERE, NOT IN THE UI. A component that hardcodes 0.8
 * is a component that disagrees with the matcher the day either number moves.
 */
export const CONFIDENT = 0.82;
export const UNSURE = 0.55;

/** How many candidates a caller gets back, always, even on a perfect hit. */
const KEEP = 3;

/**
 * Text that appears on roster screenshots and is never a player.
 *
 * 🔴 THIS LIST IS A GUARD AGAINST TUNING THE MATCHER INTO MATCHING MUSH. Every
 * one of these is a string a fuzzy scorer will happily attach to some name if
 * the thresholds drift. They are asserted as non-matches in the tests, so a
 * future loosening of the scoring fails loudly instead of quietly filling
 * somebody's ladder with a row that says "Total".
 */
const STOP_WORDS = new Set([
  "bench", "bn", "starters", "starter", "total", "totals", "empty", "player",
  "players", "pos", "position", "price", "paid", "cost", "salary", "team",
  "teams", "roster", "lineup", "opp", "proj", "pts", "points", "avg", "rank",
  "flex", "superflex", "sflex", "wrrbte", "wrrb", "rbwrte", "ir", "taxi",
  "qb", "rb", "wr", "te", "k", "def", "dst", "pk", "d", "st",
  // 🔴 APP CHROME. A league board is mostly headings, and a PAIR of them slips
  // past a whole-string check: "Teams Picks" off a Yahoo draft board matched
  // Dontayvion Wicks well enough to take a starting slot on a real roster.
  "draft", "drafts", "results", "result", "picks", "avg", "salary",
  "league", "leagues", "standings", "matchup", "matchups", "settings", "back",
]);

/** Levenshtein distance. Two rows, because the pool is walked for every read. */
function editDistance(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = new Array(b.length + 1);
  let curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= b.length; j++) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    const swap = prev;
    prev = curr;
    curr = swap;
  }
  return prev[b.length];
}

/** Edit distance as a 0-1 similarity, normalized by the longer string. */
function similarity(a, b) {
  const longest = Math.max(a.length, b.length);
  if (!longest) return 0;
  return 1 - editDistance(a, b) / longest;
}

function surnameOf(normalized) {
  const parts = normalized.split(" ").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : "";
}

function forenameOf(normalized) {
  const parts = normalized.split(" ").filter(Boolean);
  return parts.length > 1 ? parts[0] : "";
}

/**
 * Score one candidate against one normalized read.
 *
 * Three readings, best one wins, because the same mangling shows up in three
 * different shapes and no single measure catches all of them:
 *
 *  1. WHOLE STRING. Catches character-level noise, which is most of it:
 *     "bijan r0binson", "jaien hurts", "joshallen".
 *  2. INITIAL FORM. Fantasy screenshots abbreviate forenames far more often
 *     than surnames ("J. Chase", "A. St. Brown"). A whole-string measure reads
 *     those as terrible; they are actually near certain when the surname is
 *     solid and the initial agrees.
 *  3. SURNAME-WEIGHTED. Catches a badly-read forename beside a clean surname.
 *     Deliberately capped below 1 so it can never beat a real whole-string
 *     match: a surname alone is evidence, not proof, and two players can share
 *     one.
 */
function scoreCandidate(readNorm, candNorm) {
  const whole = similarity(readNorm, candNorm);

  const readSur = surnameOf(readNorm);
  const candSur = surnameOf(candNorm);
  const readFore = forenameOf(readNorm);
  const candFore = forenameOf(candNorm);

  let initial = 0;
  if (readFore.length === 1 && candFore && readFore[0] === candFore[0]) {
    // The forename is an initial and it agrees. Everything now rests on the
    // surname, so score the surname and give back most of what abbreviating
    // cost, but not all of it: "J. Smith" is genuinely ambiguous and should
    // still arrive flagged rather than silent if two Smiths exist.
    initial = 0.55 + 0.45 * similarity(readSur, candSur);
  }

  let surnameWeighted = 0;
  if (readSur && candSur) {
    const sur = similarity(readSur, candSur);
    const fore = readFore && candFore ? similarity(readFore, candFore) : 0;
    surnameWeighted = Math.min(0.97, 0.7 * sur + 0.3 * fore);
  }

  return Math.max(whole, initial, surnameWeighted);
}

/**
 * Resolve one read string to a pool entry.
 *
 * @param raw   what the reader produced for this row. Anything, including junk.
 * @param opts  { pos, team } when the row carried them. Both optional; `pos` is
 *              a hard filter when present, `team` only breaks ties.
 * @returns { player, pos, team, score, candidates }. `player` is null below
 *          UNSURE, and `candidates` is still populated so a UI can offer them.
 */
export function matchPoolName(raw, opts = {}) {
  const empty = { player: null, pos: null, team: null, score: 0, candidates: [] };
  if (raw == null) return empty;

  const readNorm = normName(String(raw));
  if (!readNorm) return empty;

  // 🔴 Junk is rejected BEFORE scoring, not by hoping the score comes out low.
  // A bare number and a column header are not weak matches, they are not names.
  // 🔴 EVERY TOKEN, NOT JUST THE WHOLE STRING. A single heading was already
  // rejected, but a PAIR of them was not: "Teams Picks" off a Yahoo league
  // board matched Dontayvion Wicks and took a starting slot. If nothing in a
  // line is a word a roster would contain, the line is not a player.
  const tokens = readNorm.split(" ").filter(Boolean);
  if (tokens.length && tokens.every((t) => STOP_WORDS.has(t))) return empty;
  if (STOP_WORDS.has(readNorm)) return empty;
  if (/^[0-9$.,\s]+$/.test(readNorm)) return empty;

  const wantGroup = opts.pos ? projGroup(opts.pos) : null;
  const wantTeam = opts.team ? String(opts.team).toUpperCase() : null;

  const inFilter = (entry) => !wantGroup || projGroup(entry.pos) === wantGroup;

  /**
   * 🔴 AN ABBREVIATED FORENAME IS AMBIGUOUS EVIDENCE AND MUST ARRIVE FLAGGED.
   *
   * Measured, not assumed: a full-pool noise sweep found SIX silent wrong
   * fills, every one of them an initial form scoring a perfect 1.00.
   * "J. Williams" is Jamaal AND Javonte. "A. St. Brown" collides with
   * "A.J. Brown". "B. Robinson Jr." collides with Bijan. "T. Etienne" is
   * Travis or Trevor. The surname matched exactly, so the initial reading
   * scored 1.0, so the row filled silently with the wrong player and nothing
   * told the user to look.
   *
   * The fix is not a lower threshold, which would only push good matches into
   * review. It is that ambiguity is a PROPERTY OF THE POOL and has to be
   * counted: when more than one pool entry in this position group shares the
   * initial and the surname, every one of them is capped just below CONFIDENT
   * so the UI flags the row and offers the candidates. When the pair is
   * unique, "J. Chase" stays confident, because it is.
   */
  const readSur = surnameOf(readNorm);
  const readFore = forenameOf(readNorm);
  const isInitialForm = readFore.length === 1;
  let collisions = 0;
  if (isInitialForm) {
    for (const entry of ENTRY_POOL) {
      if (!inFilter(entry)) continue;
      const candNorm = normName(entry.name);
      const candFore = forenameOf(candNorm);
      if (!candFore || candFore[0] !== readFore[0]) continue;
      if (similarity(surnameOf(candNorm), readSur) >= 0.9) collisions++;
    }
  }
  const ambiguityCap = isInitialForm && collisions > 1 ? CONFIDENT - 0.01 : 1;

  const scored = [];
  ENTRY_POOL.forEach((entry, rank) => {
    if (!inFilter(entry)) return;
    let score = scoreCandidate(readNorm, normName(entry.name));
    // Team agreement is a tiebreaker and nothing more. It nudges within a
    // rounding error so it can reorder two near-identical candidates without
    // ever lifting a bad one over a good one.
    if (wantTeam && entry.team && String(entry.team).toUpperCase() === wantTeam) {
      score = Math.min(1, score + 0.02);
    }
    // `rank` is the entry's position in ENTRY_POOL, which is the board in value
    // order. See the tie-break note below for why that is worth carrying.
    scored.push({ player: entry.name, pos: entry.pos, team: entry.team || null, score, rank });
  });

  if (!scored.length) return empty;

  /**
   * 🔴 SORT ON THE RAW SCORE, AND CAP ONLY WHAT IS REPORTED.
   *
   * The first cut applied the ambiguity cap inside the scoring loop, which
   * flattened every good candidate to the same number and destroyed the
   * ranking among them. Measured: "J. Bates" returned Jake Bates, Jesse James
   * and John Bates ALL at exactly 0.81, so which one won was arbitrary, and a
   * candidate with a raw score of 0.73 was tied with one at 1.00. Capping is
   * meant to force a human look, not to make the ordering meaningless.
   *
   * 🔴 AND THE TIE-BREAK IS THE BOARD, NOT THE ALPHABET.
   *
   * Measured on Ben's own screenshot: "K. Allen" scores Keenan Allen (WR, pool
   * rank 257) and Kaytron Allen (RB, rank 351) identically, because both are a
   * K and an Allen and nothing in the string separates them. Alphabetically
   * "Kaytron" wins, so the roster got a rookie back instead of a starting
   * receiver. ENTRY_POOL is the board in value order, so an earlier entry is
   * the more prominent player, and when the text cannot decide, the more
   * prominent player is the better guess. It is still flagged either way; this
   * only decides which name is sitting there when the user looks.
   */
  scored.sort((a, b) => b.score - a.score || a.rank - b.rank);
  const candidates = scored.slice(0, KEEP).map((c) => ({
    player: c.player,
    pos: c.pos,
    team: c.team,
    score: Math.min(c.score, ambiguityCap),
  }));
  const best = candidates[0];

  if (best.score < UNSURE) {
    return { player: null, pos: null, team: null, score: best.score, candidates };
  }
  return { ...best, candidates };
}

/** Whether a match is good enough to fill a row without flagging it. */
export function isConfident(match) {
  return !!match && match.player != null && match.score >= CONFIDENT;
}
